import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Card, CardContent, Table, TableHead, TableRow, TableCell, TableBody,
  Chip, CircularProgress, Grid, TextField, MenuItem, Button, Tooltip, Stack,
} from '@mui/material';
import { VerifiedUser, OpenInNew, WarningAmber } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';

const KYC_COLOR: Record<string, any> = {
  approved: 'success', in_review: 'warning', rejected: 'error', not_started: 'default',
};
const KYC_LABEL: Record<string, string> = {
  approved: 'Approved', in_review: 'In review', rejected: 'Rejected', not_started: 'Not started',
};

const daysUntil = (d: string | null) => {
  if (!d) return null;
  const ms = new Date(d).getTime() - Date.now();
  return Math.ceil(ms / 86400000);
};

export const CompliancePage: React.FC = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');

  const load = () => {
    setLoading(true);
    api.get('/compliance/overview')
      .then(r => setRows(Array.isArray(r.data) ? r.data : []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const counts = {
    total: rows.length,
    approved: rows.filter(r => r.kyc_status === 'approved').length,
    in_review: rows.filter(r => r.kyc_status === 'in_review').length,
    not_started: rows.filter(r => r.kyc_status === 'not_started').length,
    rejected: rows.filter(r => r.kyc_status === 'rejected').length,
  };
  const data = filter ? rows.filter(r => r.kyc_status === filter) : rows;

  const StatCard = ({ label, value, color }: any) => (
    <Grid item xs={6} sm={2.4}>
      <Card variant="outlined" sx={{ cursor: 'pointer', borderColor: filter === color?.key ? 'primary.main' : undefined }}
        onClick={() => setFilter(color?.key === filter ? '' : (color?.key || ''))}>
        <CardContent sx={{ py: 1.5, textAlign: 'center' }}>
          <Typography variant="h4" fontWeight={800} color={color?.c || 'text.primary'}>{value}</Typography>
          <Typography variant="caption" color="text.secondary">{label}</Typography>
        </CardContent>
      </Card>
    </Grid>
  );

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <VerifiedUser color="primary" />
        <Typography variant="h5" fontWeight={800}>Compliance / KYC</Typography>
        <Box sx={{ flex: 1 }} />
        <Button size="small" onClick={load}>Refresh</Button>
      </Stack>

      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        <StatCard label="Customers" value={counts.total} color={{ key: '' }} />
        <StatCard label="Approved" value={counts.approved} color={{ key: 'approved', c: 'success.main' }} />
        <StatCard label="In review" value={counts.in_review} color={{ key: 'in_review', c: 'warning.main' }} />
        <StatCard label="Not started" value={counts.not_started} color={{ key: 'not_started', c: 'text.secondary' }} />
        <StatCard label="Rejected" value={counts.rejected} color={{ key: 'rejected', c: 'error.main' }} />
      </Grid>

      <Card>
        <CardContent sx={{ pb: 0 }}>
          <TextField select size="small" label="Filter status" value={filter} onChange={e => setFilter(e.target.value)} sx={{ minWidth: 180, mb: 1 }}>
            <MenuItem value="">All</MenuItem>
            <MenuItem value="approved">Approved</MenuItem>
            <MenuItem value="in_review">In review</MenuItem>
            <MenuItem value="not_started">Not started</MenuItem>
            <MenuItem value="rejected">Rejected</MenuItem>
          </TextField>
        </CardContent>
        {loading ? <Box sx={{ p: 6, textAlign: 'center' }}><CircularProgress /></Box> : (
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead><TableRow>
                {['Customer', 'Tier', 'KYC status', 'Documents', 'RMD', 'No-spoof attested', 'Next doc expiry', ''].map(h =>
                  <TableCell key={h} sx={{ fontWeight: 700 }}>{h}</TableCell>)}
              </TableRow></TableHead>
              <TableBody>
                {data.map(r => {
                  const d = daysUntil(r.next_expiry);
                  const expSoon = d !== null && d <= 30;
                  return (
                    <TableRow key={r.id} hover>
                      <TableCell sx={{ fontWeight: 600 }}>{r.name}</TableCell>
                      <TableCell><Chip label={r.tier} size="small" variant="outlined" /></TableCell>
                      <TableCell><Chip label={KYC_LABEL[r.kyc_status] || r.kyc_status} size="small" color={KYC_COLOR[r.kyc_status] || 'default'} /></TableCell>
                      <TableCell>
                        <Chip label={`${r.doc_approved}/${r.doc_total} approved`} size="small" variant="outlined"
                          color={Number(r.doc_total) === 0 ? 'default' : Number(r.doc_approved) === Number(r.doc_total) ? 'success' : 'warning'} />
                        {Number(r.doc_pending) > 0 && <Chip label={`${r.doc_pending} pending`} size="small" sx={{ ml: 0.5 }} />}
                        {Number(r.doc_expired) > 0 && <Chip label={`${r.doc_expired} expired`} size="small" color="error" sx={{ ml: 0.5 }} />}
                      </TableCell>
                      <TableCell>{r.rmd_registered ? <Chip label="yes" size="small" color="success" /> : <Chip label="no" size="small" variant="outlined" />}</TableCell>
                      <TableCell>{r.no_spoofing_attested ? <Chip label="yes" size="small" color="success" /> : <Chip label="no" size="small" variant="outlined" />}</TableCell>
                      <TableCell sx={{ color: expSoon ? 'error.main' : 'text.secondary' }}>
                        {r.next_expiry ? <>{expSoon && <WarningAmber fontSize="inherit" sx={{ mr: 0.5, verticalAlign: 'middle' }} />}{r.next_expiry}{d !== null && ` (${d}d)`}</> : '—'}
                      </TableCell>
                      <TableCell>
                        <Tooltip title="Open KYC"><Button size="small" endIcon={<OpenInNew />} onClick={() => navigate(`/customers/${r.id}?tab=kyc`)}>Manage</Button></Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {data.length === 0 && <TableRow><TableCell colSpan={8} align="center" sx={{ py: 6, color: 'text.secondary' }}>No customers match.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </Box>
        )}
      </Card>
    </Box>
  );
};
